import React from 'react';
import { validated } from 'react-custom-validation'
import PropTypes from 'prop-types';
import update from 'immutability-helper';

import { handleSelectChange, handleTextChange } from 'utils/handle-changes';
import { isEmail, isRequired } from 'utils/validations';

import { STATES } from 'utils/constants';

import AvatarUpload from 'components/AvatarUpload/index';
import Button from 'components/Button/index';
import Dropdown from 'components/Dropdown/index';
import Form from 'components/Form/index';
import FormColumn from 'components/FormColumn/index';
import FormRow from 'components/FormRow/index';
import FormValidationMessage from 'components/FormValidationMessage/index';
import H3 from 'components/H3/index';
import LoadingCircle from 'components/LoadingCircle/index';
import Textbox from 'components/Textbox/index';
import clone from "clone";

class AgencyAccountForm extends React.Component {
    static propTypes = {
        adminView: PropTypes.bool.isRequired,
        avatarError: PropTypes.object.isRequired,
        company: PropTypes.object.isRequired,
        error: PropTypes.object.isRequired,
        isSaving: PropTypes.bool.isRequired,
        isSavingAvatar: PropTypes.bool.isRequired,
        locations: PropTypes.array.isRequired,
        onInvalid: PropTypes.func.isRequired,
        onValid: PropTypes.func.isRequired,
        setCompany: PropTypes.func.isRequired,
        showChangeNotification: PropTypes.bool.isRequired,
        skills: PropTypes.array.isRequired,
        specialties: PropTypes.array.isRequired,
        uploadAvatar: PropTypes.func.isRequired,
        user: PropTypes.object.isRequired
    };

    constructor() {
        super();

        this.state = {
            originalCompany: {}
        };
    }

    componentDidMount() {
        this.setState({originalCompany: clone(this.props.user.company)});
    }

    _handleChange(name, value) {
        console.log(name, value);
        if(['website', 'linkedIn', 'dribbble', 'twitter', 'facebook', 'instagram', 'github'].indexOf(name) !== -1) {
            value = value.replace('https://', '');
            value = value.replace('http://', '');
        }

        let newCompanyObj = update(this.props.company, {});
        newCompanyObj[name] = value;
        this.props.setCompany(newCompanyObj);
    }

    _handleAvatarSelect(files) {
        this._handleChange('profilePicUrl', files[0]);
        this.props.uploadAvatar();
    }

    render() {
        let { adminView, avatarError, company, error, isSaving, onInvalid, onValid, user, $field, $validation } = this.props;

        if((Object.keys(company).length === 0 || Object.keys(user).length === 0) && adminView !== true) {
            return (
                <LoadingCircle />
            );
        } else {
            return (
                <Form className="agency-account-form">
                    {adminView !== true ? (
                        <FormRow className="agency-account-form__avatar-upload--wrapper">
                            <AvatarUpload
                                avatar={company.profilePicUrl}
                                className="agency-account-form__avatar-upload"
                                handleAvatarSelect={this._handleAvatarSelect.bind(this)}
                                instructions="Upload your business logo. Recommended image size is 480px by 480px, png or jpeg format, less than 2mb."
                                isSaving={this.props.isSavingAvatar === true}
                            />

                            {Object.keys(avatarError).length > 0 ? (
                                <FormValidationMessage>
                                    {avatarError.friendlyMessage || avatarError.message}
                                </FormValidationMessage>
                            ) : null}
                        </FormRow>
                    ) : null}

                    <FormRow columnCount={2}>
                        <FormColumn>
                            <H3>
                                Basic Information
                            </H3>

                            <FormRow>
                                <Textbox
                                    id="txtName"
                                    label="Company"
                                    name="name"
                                    required
                                    type="text"
                                    validation={$validation.name}
                                    value={company.name || ''}
                                    {...$field('name', (event) => handleTextChange(this._handleChange.bind(this), event))}
                                />
                            </FormRow>

                            {user.isAdmin === true ? (
                                <FormRow>
                                    <Dropdown
                                        id="selLocation"
                                        label="Location/Market"
                                        labelKey="name"
                                        name="locationId"
                                        onChange={handleSelectChange.bind(this, this._handleChange.bind(this), 'locationId', null, null)}
                                        options={this.props.locations}
                                        placeholder="Choose Market"
                                        value={company.locationId}
                                        valueKey="id"
                                    />
                                </FormRow>
                            ) : null}

                            <FormRow>
                                <Textbox
                                    id="txtAbout"
                                    label="About"
                                    name="description"
                                    type="textarea"
                                    onChange={(event) => handleTextChange(this._handleChange.bind(this), event)}
                                    value={company.description || ''}
                                />
                            </FormRow>

                            <FormRow>
                                <Textbox
                                    id="txtAddress"
                                    label="Address"
                                    name="streetAddress"
                                    type="text"
                                    onChange={(event) => handleTextChange(this._handleChange.bind(this), event)}
                                    value={company.streetAddress || ''}
                                />
                            </FormRow>

                            <FormRow className="a-form-row--city-state-zip"
                                     columnCount={3}>
                                <FormColumn>
                                    <Textbox
                                        id="txtCity"
                                        label="City"
                                        name="city"
                                        type="text"
                                        onChange={(event) => handleTextChange(this._handleChange.bind(this), event)}
                                        value={company.city || ''}
                                    />
                                </FormColumn>

                                <FormColumn>
                                    <Dropdown
                                        id="selState"
                                        label="State"
                                        name="state"
                                        onChange={handleSelectChange.bind(this, this._handleChange.bind(this), 'state', null, null)}
                                        options={STATES}
                                        placeholder=""
                                        value={company.state}
                                    />
                                </FormColumn>

                                <FormColumn>
                                    <Textbox
                                        id="txtZip"
                                        label="Zip"
                                        name="zip"
                                        type="text"
                                        onChange={(event) => handleTextChange(this._handleChange.bind(this), event)}
                                        value={company.zip || ''}
                                    />
                                </FormColumn>
                            </FormRow>
                        </FormColumn>

                        <FormColumn>
                            <H3>
                                Contact Information
                            </H3>

                            <FormRow>
                                <Textbox
                                    id="txtEmail"
                                    label="Email"
                                    name="email"
                                    required
                                    type="email"
                                    validation={$validation.email}
                                    value={company.email || ''}
                                    {...$field('email', (event) => handleTextChange(this._handleChange.bind(this), event))}
                                />
                            </FormRow>

                            <FormRow>
                                <Textbox
                                    id="txtPhone"
                                    label="Phone"
                                    name="phoneNumber"
                                    type="text"
                                    onChange={(event) => handleTextChange(this._handleChange.bind(this), event)}
                                    value={company.phoneNumber || ''}
                                />
                            </FormRow>

                            <FormRow>
                                <Textbox
                                    id="txtWebsite"
                                    className="a-textbox--link"
                                    label="Website"
                                    name="website"
                                    type="text"
                                    onChange={(event) => handleTextChange(this._handleChange.bind(this), event)}
                                    value={company.website || ''}
                                />
                            </FormRow>
                        </FormColumn>
                    </FormRow>

                    {Object.keys(error).length > 0 ? (
                        <FormRow className="error-message">
                            <FormValidationMessage>
                                {error.friendlyMessage || error.message}
                            </FormValidationMessage>
                        </FormRow>
                    ) : null}

                    <FormRow className="button-wrapper">
                        <Button disabled={isSaving === true}
                                onClick={(event) => {
                                    event.preventDefault();
                                    this.props.$submit(onValid, onInvalid);
                                }}
                                type="submit">
                            {adminView !== true ? 'Update' : 'Create'}
                            {isSaving === true ? <LoadingCircle innerColor="#ffffff" outerColor="#ffffff"/> : null}
                        </Button>
                    </FormRow>
                </Form>
            );
        }
    }
}

function agencyAccountFormValidationConfig(props) {
    const { email, name } = props.company;

    return {
        fields: ['email', 'name'],
        validations: {
            email: [
                [isRequired, email],
                [isEmail, email]
            ],
            name: [
                [isRequired, name]
            ]
        }
    }
}

export default AgencyAccountForm = validated(agencyAccountFormValidationConfig)(AgencyAccountForm);



// WEBPACK FOOTER //
// ./src/containers/Accounts/Edit/AgencyAccountForm.js